<template>
  <div class="online-application">
    <div class="hero is-primary">
      <div class="hero-body">
        <div class="container has-text-centered">
          <h1 class="title has-text-white">
            <span class="icon is-large">
              <i class="fa fa-credit-card"></i>
            </span>
            {{ $t("online loan application") }}
          </h1>
          <h2 class="subtitle has-text-white">
            {{ $t("complete the following steps to process your application") }}
          </h2>
        </div>
      </div>
    </div>

    <div class="container mt-5">
      <div class="columns">
        <div class="column is-8 is-offset-2">
          <StepsIndicator
            :current-step="currentStep"
            :total-steps="totalSteps"
            :steps="steps"
          />

          <div class="box mt-5">
            <PersonalInformationStep
              v-if="currentStep === 1"
              :form-data="formData"
              :errors="errors"
              :is-loading-zip="isLoadingZip"
              @update:nombres="(value) => handleUpdateField('nombres', value)"
              @update:apellidos="
                (value) => handleUpdateField('apellidos', value)
              "
              @update:email="(value) => handleUpdateField('email', value)"
              @update:fecha-nacimiento="
                (value) => handleUpdateField('fechaNacimiento', value)
              "
              @update:telefono-usa="
                (value) => handleUpdateField('telefonoUSA', value)
              "
              @handle-zipcode-input="handleZipCodeInput"
              @update:ingresos-mensuales="
                (value) => handleUpdateField('ingresosMensuales', value)
              "
              @update:monto-credito="
                (value) => handleUpdateField('montoCredito', value)
              "
              @validate-field="handleValidateField"
            />

            <ReferencesStep
              v-else-if="currentStep === 2"
              :form-data="formData"
              :errors="errors"
              @update-us-reference-name="handleUpdateUSReferenceName"
              @update-guatemala-reference-name="
                handleUpdateGuatemalaReferenceName
              "
              @update-us-phone-reference="handleUpdateUSPhoneReference"
              @update-guatemala-phone-reference="
                handleUpdateGuatemalaPhoneReference
              "
              @add-us-reference="handleAddUSReference"
              @remove-us-reference="handleRemoveUSReference"
              @add-guatemala-reference="handleAddGuatemalaReference"
              @remove-guatemala-reference="handleRemoveGuatemalaReference"
            />

            <DocumentationStep
              v-else-if="currentStep === 3"
              :form-data="formData"
              :errors="errors"
              :previews="previews"
              :selfie-preview="selfiePreview"
              @update:tipo-documento="updateTipoDocumento"
              @update:numero-documento="
                (value) => handleUpdateField('numeroDocumento', value)
              "
              @handle-file-upload="handleFileUpload"
              @selfie-captured="handleSelfieCapture"
              @validate-field="handleValidateField"
            />

            <div v-else-if="currentStep === 4" class="has-text-centered">
              <div class="icon is-large has-text-success">
                <i class="fa fa-check-circle fa-3x"></i>
              </div>
              <h2 class="title is-4 mt-4">{{ $t("application completed") }}</h2>
              <p class="subtitle is-6">
                {{ $t("application sent successfully message") }}
              </p>
              <div class="notification is-info">
                <strong>{{ $t("application number") }}:</strong>
                {{ numeroSolicitud }}
              </div>
            </div>

            <div class="field is-grouped mt-5">
              <div class="control">
                <button
                  v-if="currentStep > 1 && currentStep < 4"
                  class="button is-light"
                  @click="previousStep"
                >
                  <span class="icon">
                    <i class="fa fa-arrow-left"></i>
                  </span>
                  <span>{{ $t("previous") }}</span>
                </button>
              </div>

              <div class="control">
                <button
                  v-if="currentStep < 3"
                  class="button is-primary"
                  @click="nextStep"
                  :disabled="!canProceed"
                >
                  <span>{{ $t("next") }}</span>
                  <span class="icon">
                    <i class="fa fa-arrow-right"></i>
                  </span>
                </button>

                <!-- Debug button - temporary -->
                <button
                  v-if="currentStep === 3"
                  class="button is-info is-small"
                  @click="debugFormState"
                  style="margin-right: 10px"
                >
                  Debug Form
                </button>

                <button
                  v-if="currentStep === 3"
                  class="button is-success"
                  @click="submitApplication"
                  :disabled="isSubmitting || !isFormValid"
                  :class="{ 'is-loading': isSubmitting }"
                >
                  <span class="icon">
                    <i class="fa fa-paper-plane"></i>
                  </span>
                  <span>{{ $t("submit application") }}</span>
                </button>

                <!-- <button
                  v-if="currentStep === 4"
                  class="button is-primary"
                  @click="startNewApplication"
                >
                  <span class="icon">
                    <i class="fa fa-plus"></i>
                  </span>
                  <span>{{ $t("new application") }}</span>
                </button> -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import StepsIndicator from "./components/StepsIndicator.vue";
import PersonalInformationStep from "./components/PersonalInformationStep.vue";
import ReferencesStep from "./components/ReferencesStep.vue";
import DocumentationStep from "./components/DocumentationStep.vue";
import { useFormData } from "./composables/useFormData.js";
import { useSteps } from "./composables/useSteps.js";
import { useValidation } from "./composables/useValidation.js";
import { lookupZipCode } from "./utils/zipCodeService.js";
import {
  validateUSPhone,
  validateGuatemalaPhone,
  validateCurrency,
} from "./utils/formatters.js";
import { post } from "@/utils";

export default {
  name: "OnlineApplication",
  components: {
    StepsIndicator,
    PersonalInformationStep,
    ReferencesStep,
    DocumentationStep,
  },
  setup() {
    // Composables
    const { formData, updateFormData, resetForm } = useFormData();
    const { currentStep, totalSteps, steps, nextStep, previousStep, goToStep } =
      useSteps();
    const selfiePreview = ref(null);

    // Local state
    const isSubmitting = ref(false);
    const numeroSolicitud = ref("");
    const previews = ref({});
    const errors = ref({});
    const isLoadingZip = ref(false);

    const {
      validateField,
      validateForm,
      formProgress,
      stepProgress,
      canProceedToNextStep,
      isFormValid,
    } = useValidation(formData, errors, steps, currentStep);

    // Computed
    const canProceed = computed(() => {
      return canProceedToNextStep.value;
    });

    // Methods
    const updateTipoDocumento = (value) => {
      updateFormData("tipoDocumento", value);
    };

    const handleFileUpload = (fieldName, event) => {
      const file = event.target.files[0];
      if (!file) return;

      // Validate file
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        errors.value[fieldName] = "El archivo es demasiado grande (máximo 5MB)";
        return;
      }

      const validTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
      ];
      if (!validTypes.includes(file.type)) {
        errors.value[fieldName] = "Tipo de archivo no válido";
        return;
      }

      // Update form data
      updateFormData(fieldName, file);

      if (fieldName === "selfie") {
        console.log("Selfie file uploaded:", {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        });
      }

      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (fieldName === "selfie") {
            selfiePreview.value = e.target.result;
            console.log("Selfie preview actualizada");
          } else {
            previews.value[fieldName] = e.target.result;
          }
        };
        reader.readAsDataURL(file);
      }

      // Clear error
      if (errors.value[fieldName]) {
        delete errors.value[fieldName];
      }

      if (fieldName === "selfie") {
        console.log("After selfie upload - Form progress:", formProgress.value);
        console.log("After selfie upload - Is form valid:", isFormValid.value);
      }
    };

    const handleSelfieCapture = (data) => {
      console.log("handleSelfieCapture called with:", data);

      if (data.file && data.preview) {
        // Selfie capturada con cámara
        updateFormData("selfie", data.file);
        selfiePreview.value = data.preview;
        console.log("Selfie actualizada en formData:", {
          hasFile: !!formData.selfie,
          fileName: formData.selfie?.name,
          fileSize: formData.selfie?.size,
        });
      } else {
        // Retake - limpiar selfie
        updateFormData("selfie", null);
        selfiePreview.value = null;
        console.log("Selfie limpiada");
      }

      // Clear error
      if (errors.value.selfie) {
        delete errors.value.selfie;
      }

      // Forzar validación del formulario para actualizar isFormValid
      console.log("Form progress:", formProgress.value);
      console.log("Is form valid:", isFormValid.value);
      console.log("Current formData.selfie:", !!formData.selfie);
    };

    const submitApplication = async () => {
      if (!isFormValid.value) {
        return;
      }

      isSubmitting.value = true;

      try {
        // Create FormData for file upload
        const submitData = new FormData();

        // Add all form fields
        Object.keys(formData).forEach((key) => {
          const value = formData[key];
          // Skip selfie here, we'll handle it separately to avoid duplication
          if (key === "selfie") {
            return;
          }

          if (value instanceof File) {
            submitData.append(key, value);
          } else if (typeof value === "object" && value !== null) {
            submitData.append(key, JSON.stringify(value));
          } else {
            submitData.append(key, value);
          }
        });

        // Add selfie - prioritize the file from formData, fallback to captured selfie
        if (formData.selfie && formData.selfie instanceof File) {
          // Selfie was uploaded as file
          submitData.append("selfie", formData.selfie);
        } else if (selfiePreview.value) {
          // Selfie was captured with camera
          const response = await fetch(selfiePreview.value);
          const blob = await response.blob();
          submitData.append("selfie", blob, "selfie.jpg");
        }

        post("/loan/application/online", submitData)
          .then((rspns) => {
            console.log("Application submitted successfully:", rspns);
            numeroSolicitud.value = rspns.applicationNumber || "N/A";
            goToStep(4);
          })
          .catch((error) => {
            console.error("Error submitting application:", error);
            // Handle error appropriately
          })
          .finally(() => {
            isSubmitting.value = false;
          });
        // Submit application (replace with actual API call)
        // await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulate API call

        // // Generate application number
        // numeroSolicitud.value = "SOL-" + Date.now();

        // // Go to success step
        // goToStep(4);
      } catch (error) {
        console.error("Error submitting application:", error);
        // Handle error appropriately
      } finally {
        isSubmitting.value = false;
      }
    };

    const startNewApplication = () => {
      resetForm();
      previews.value = {};
      numeroSolicitud.value = "";
      goToStep(1);
    };

    // Personal Information Step handlers
    const handleUpdateField = (field, value) => {
      updateFormData(field, value);
    };

    const handleZipCodeInput = async (value) => {
      updateFormData("zipCode", value);

      if (value.length === 5) {
        isLoadingZip.value = true;
        try {
          const result = await lookupZipCode(value);
          updateFormData("estado", result.state);
          updateFormData("ciudad", result.city);
        } catch (error) {
          console.error("Error looking up zip code:", error);
          // Clear state and city on error
          updateFormData("estado", "");
          updateFormData("ciudad", "");
        } finally {
          isLoadingZip.value = false;
        }
      } else {
        // Clear state and city if zip code is incomplete
        updateFormData("estado", "");
        updateFormData("ciudad", "");
      }
    };

    const handleValidateField = (fieldName) => {
      validateField(fieldName);
    };

    // References handlers
    const handleUpdateUSPhoneReference = (index, value) => {
      const references = [...formData.usReferences];
      references[index].telefono = value;
      updateFormData("usReferences", references);
    };

    const handleUpdateGuatemalaPhoneReference = (index, value) => {
      const references = [...formData.guatemalaReferences];
      references[index].telefono = value;
      updateFormData("guatemalaReferences", references);
    };

    const handleUpdateUSReferenceName = (index, value) => {
      const references = [...formData.usReferences];
      references[index].nombre = value;
      updateFormData("usReferences", references);
    };

    const handleUpdateGuatemalaReferenceName = (index, value) => {
      const references = [...formData.guatemalaReferences];
      references[index].nombre = value;
      updateFormData("guatemalaReferences", references);
    };

    const handleAddUSReference = () => {
      const references = [
        ...formData.usReferences,
        { nombre: "", telefono: "" },
      ];
      updateFormData("usReferences", references);
    };

    const handleRemoveUSReference = (index) => {
      const references = formData.usReferences.filter((_, i) => i !== index);
      updateFormData("usReferences", references);
    };

    const handleAddGuatemalaReference = () => {
      const references = [
        ...formData.guatemalaReferences,
        { nombre: "", telefono: "" },
      ];
      updateFormData("guatemalaReferences", references);
    };

    const handleRemoveGuatemalaReference = (index) => {
      const references = formData.guatemalaReferences.filter(
        (_, i) => i !== index
      );
      updateFormData("guatemalaReferences", references);
    };

    return {
      // State
      formData,
      currentStep,
      totalSteps,
      steps,
      errors,
      isSubmitting,
      numeroSolicitud,
      previews,
      selfiePreview,
      isLoadingZip,

      // Computed
      canProceed,
      isFormValid,

      // Methods
      updateFormData,
      updateTipoDocumento,
      nextStep,
      previousStep,
      handleFileUpload,
      handleSelfieCapture,
      submitApplication,
      startNewApplication,
      lookupZipCode,

      // Personal Information handlers
      handleUpdateField,
      handleZipCodeInput,
      handleValidateField,

      // References handlers
      handleUpdateUSPhoneReference,
      handleUpdateGuatemalaPhoneReference,
      handleUpdateUSReferenceName,
      handleUpdateGuatemalaReferenceName,
      handleAddUSReference,
      handleRemoveUSReference,
      handleAddGuatemalaReference,
      handleRemoveGuatemalaReference,

      // Debug function - temporary
      debugFormState: () => {
        console.log("=== DEBUG FORM STATE ===");
        console.log("Form Progress:", formProgress.value);
        console.log("Is Form Valid:", isFormValid.value);
        console.log("Current Step:", currentStep.value);
        console.log("Errors:", errors.value);

        // Debug progress calculation
        const requiredFields = [
          "nombres",
          "apellidos",
          "email",
          "telefonoUSA",
          "zipCode",
          "ingresosMensuales",
          "montoCredito",
          "tipoDocumento",
        ];

        console.log("=== REQUIRED FIELDS CHECK ===");
        requiredFields.forEach((field) => {
          let isComplete = false;
          if (field === "ingresosMensuales" || field === "montoCredito") {
            isComplete = formData[field] > 0;
          } else {
            isComplete = formData[field] && formData[field].trim();
          }
          console.log(
            `${field}: ${formData[field]} -> ${isComplete ? "✓" : "✗"}`
          );
        });

        const completedFields = requiredFields.filter((field) => {
          if (field === "ingresosMensuales" || field === "montoCredito") {
            return formData[field] > 0;
          }
          return formData[field] && formData[field].trim();
        });

        const fileFields = [
          "documentoFrontal",
          "documentoTrasero",
          "comprobanteDomicilio",
          "selfie",
        ];
        console.log("=== FILE FIELDS CHECK ===");
        fileFields.forEach((field) => {
          const hasFile = !!formData[field];
          console.log(`${field}: ${hasFile ? "✓" : "✗"}`);
        });

        const completedFiles = fileFields.filter((field) => formData[field]);

        const hasUSReference = formData.usReferences.some(
          (ref) => ref.nombre && ref.telefono
        );
        const hasGTReference = formData.guatemalaReferences.some(
          (ref) => ref.nombre && ref.telefono
        );
        const referencesValid = hasUSReference && hasGTReference;

        console.log("=== REFERENCES CHECK ===");
        console.log("US Reference Valid:", hasUSReference);
        console.log("GT Reference Valid:", hasGTReference);
        console.log("References Valid:", referencesValid);

        const totalRequired = requiredFields.length + fileFields.length + 1; // +1 for references
        const totalCompleted =
          completedFields.length +
          completedFiles.length +
          (referencesValid ? 1 : 0);

        console.log("=== PROGRESS CALCULATION ===");
        console.log("Total Required:", totalRequired);
        console.log(
          "Completed Fields:",
          completedFields.length,
          "/",
          requiredFields.length
        );
        console.log(
          "Completed Files:",
          completedFiles.length,
          "/",
          fileFields.length
        );
        console.log("References Completed:", referencesValid ? 1 : 0, "/ 1");
        console.log("Total Completed:", totalCompleted);
        console.log(
          "Progress %:",
          Math.round((totalCompleted / totalRequired) * 100)
        );

        console.log("Form Data Files:", {
          documentoFrontal: !!formData.documentoFrontal,
          documentoTrasero: !!formData.documentoTrasero,
          comprobanteDomicilio: !!formData.comprobanteDomicilio,
          selfie: !!formData.selfie,
        });
        console.log("Selfie Preview:", !!selfiePreview.value);

        alert(
          `Form Progress: ${formProgress.value}% | Is Valid: ${
            isFormValid.value
          } | Selfie: ${!!formData.selfie}`
        );
      },
    };
  },
};
</script>

<style scoped>
.online-application {
  min-height: 100vh;
  /* background-color: #f5f5f5; */
}

.box {
  box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1);
}

.hero {
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  background: none;
}

.steps-indicator {
  margin-bottom: 2rem;
}

@media (max-width: 768px) {
  .column.is-8.is-offset-2 {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }

  .container {
    padding: 0 1rem;
  }
}
</style>
